import { Box, Dialog } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { ReactNode, useCallback, useState } from 'react';
import { Button } from '../../MUI/button/Button';
import { DeleteConfirmationDialog } from '../deleteConfirmation/DeleteConfirmationDialog';
import { List } from '../list/List';
import { ShowInactiveToggle, EmptyState } from '../../UI';
import { feedback } from '../../../services';
import { useAppThemeContext } from '../../../contexts';

/** @internal — shape expected by the List component */
interface CrudListItem {
  id: number;
  description: string;
  disabled?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
}

interface CrudListPageProps<T extends { id?: number; isActive?: boolean }> {
  /** The full list of items */
  items: T[];
  /** Map each item to a List item (description, adornments, etc.) */
  mapToItem: (item: T) => CrudListItem;
  /** Whether to show inactive items */
  showInactive?: boolean;
  /** Toggle inactive filter */
  onToggleInactive?: (show: boolean) => void;
  /** Service name for data-testid (e.g. 'service', 'product') */
  entityName: string;
  /** Label for the "Novo" button */
  newButtonLabel?: string;
  /** Label for delete confirmation */
  deleteLabel?: string;
  /** Remove API call */
  removeApi: (id: number) => Promise<unknown>;
  /** Called to refetch the parent data */
  onRefetch: () => void;
  /** Optional extra controls rendered alongside the "Novo" button */
  extraControls?: ReactNode;
  /** Optional custom empty state message when showInactive is false */
  emptyActiveMessage?: string;
  /** Optional custom empty state message when showInactive is true */
  emptyInactiveMessage?: string;
  /** Register form component rendered inside the dialog */
  RegisterForm: React.FC<{
    entityId?: number;
    businessId: number;
    refetch: () => void;
    onCloseEdit: () => void;
  }>;
  /** Business id passed to the register form */
  businessId: number;
  /** Optional content rendered at the top (inside the same container), above the toolbar */
  topContent?: ReactNode;
  /** Optional data-testid for the register dialog */
  dialogTestId?: string;
}

export function CrudListPage<T extends { id?: number; isActive?: boolean }>(
  props: CrudListPageProps<T>
) {
  const { isMobile } = useAppThemeContext();
  const [internalShowInactive, setInternalShowInactive] = useState(false);

  const {
    items,
    mapToItem,
    showInactive = internalShowInactive,
    onToggleInactive = setInternalShowInactive,
    entityName,
    newButtonLabel = 'Novo',
    deleteLabel,
    removeApi,
    onRefetch,
    extraControls,
    emptyActiveMessage,
    emptyInactiveMessage,
    RegisterForm,
    businessId,
    topContent,
    dialogTestId,
  } = props;

  const [openRegisterDialog, setOpenRegisterDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);

  const { mutate: remove, isLoading: isLoadingDelete } = useMutation(
    (id: number) => removeApi(id),
    {
      onSuccess: () => {
        onRefetch();
        handleClose();
      },
      onError: (error: Error) => feedback(String(error), 'error'),
    }
  );

  const onClickEdit = useCallback((id: number) => {
    setSelectedId(id);
    setOpenRegisterDialog(true);
  }, []);

  const onClickDelete = useCallback((id: number) => {
    setSelectedId(id);
    setOpenDeleteDialog(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpenRegisterDialog(false);
    setOpenDeleteDialog(false);
    setSelectedId(undefined);
  }, []);

  const handleStartCreate = useCallback(() => {
    setSelectedId(undefined);
    setOpenRegisterDialog(true);
  }, []);

  const visibleItems = showInactive
    ? items
    : items.filter((item) => item.isActive !== false);

  return (
    <Box display='flex' flexDirection='column' gap={3} padding={3}>
      {topContent}
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <Box display='flex' alignItems='center' gap={2}>
          {onToggleInactive && (
            <ShowInactiveToggle
              checked={showInactive}
              onChange={onToggleInactive}
            />
          )}
        </Box>
        <Box display='flex' gap={1}>
          {extraControls}
          <Button
            label={newButtonLabel}
            variant='contained'
            size={isMobile ? 'small' : 'medium'}
            onClick={handleStartCreate}
            data-testid={`btn-new-${entityName}`}
          />
        </Box>
      </Box>

      {visibleItems.length === 0 ? (
        <EmptyState
          message={
            showInactive
              ? emptyInactiveMessage ?? 'Nenhum registro cadastrado.'
              : emptyActiveMessage ?? 'Nenhum registro ativo. Marque "Mostrar inativos" para ver todos.'
          }
        />
      ) : (
        <List
          list={visibleItems.map(mapToItem)}
          onClickEdit={onClickEdit}
          onClickDelete={onClickDelete}
        />
      )}

      <Dialog open={openRegisterDialog} onClose={handleClose} {...(dialogTestId ? { 'data-testid': dialogTestId } : {})}>
        {businessId && (
          <RegisterForm
            entityId={selectedId}
            businessId={businessId}
            refetch={onRefetch}
            onCloseEdit={handleClose}
          />
        )}
      </Dialog>

      <DeleteConfirmationDialog
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={() => selectedId && remove(selectedId)}
        open={openDeleteDialog}
        isLoading={isLoadingDelete}
        label={deleteLabel ?? `Tem certeza que deseja deletar este ${entityName}?`}
      />
    </Box>
  );
}
